import MatchScoreBadge, { MatchScoreLabels } from './MatchScoreBadge';
import OrgCard from '../OrgCard';
import { getCountryName } from '../../utils/formatAddress';
import { MigrationCandidateMatchTarget } from '../../api/generated/pm';

/**
 * Joins the match target's address fields into one line. The generated type
 * declares each field optional and nullable (`string | null`; they come from a
 * JSONB column and may be missing or unparseable), and the `typeof` guard
 * additionally drops anything that is not a non-empty string at runtime. The
 * country field is expanded from its 2-letter ISO code to a display name via
 * {@link getCountryName}, falling back to the raw code when unresolvable.
 */
export function formatMatchTargetAddress(target: MigrationCandidateMatchTarget): string {
  const countryCode = target.business_address_country_code;
  const country = typeof countryCode === 'string' && countryCode.length > 0 ? countryCode : '';

  return [
    target.business_address_line_1,
    target.business_address_zip_postal_code,
    target.business_address_city,
    target.business_address_state_province_region,
    country && getCountryName(country),
  ]
    .filter((part): part is string => typeof part === 'string' && part.length > 0)
    .join(', ');
}

interface Props {
  target: MigrationCandidateMatchTarget;
  selected: boolean;
  onSelect: () => void;
  labels: MatchScoreLabels;
}

/**
 * One selectable Potential Match: the target's name, its customer number
 * formatted `###-###-###-###`, its address, and the match-score badge.
 */
function PotentialMatchRow({ target, selected, onSelect, labels }: Props) {
  const address = formatMatchTargetAddress(target);

  return (
    <OrgCard
      name={target.name}
      externalId={target.external_id}
      address={address || undefined}
      selected={selected}
      onSelect={onSelect}
      badge={<MatchScoreBadge target={target} selected={selected} labels={labels} />}
      testId={`org-row-${target.external_id}`}
    />
  );
}

export default PotentialMatchRow;
